import React, { useState } from 'react';
import OverviewTab from './matter-detail-tabs/OverviewTab.tsx';
import CommunicationsTab from './matter-detail-tabs/CommunicationsTab.tsx';
import NotesTab from './matter-detail-tabs/NotesTab.tsx';
import DocumentsTab from './matter-detail-tabs/DocumentsTab.tsx';
import BillingTab from './matter-detail-tabs/BillingTab.tsx';
import TasksTab from './matter-detail-tabs/TasksTab.tsx';
import TimelineTab from './matter-detail-tabs/TimelineTab.tsx';
import CalendarTab from './matter-detail-tabs/CalendarTab.tsx';
import PlaybookTab from '@/features/playbooks/components/PlaybookTab';
import ActiveTimerBanner from './ActiveTimerBanner';
import { useMatterBilling } from '../hooks/useMatterBilling';
import { MatterDetailed } from '../../../types/matters';

// Define tab keys as union type for type safety
type TabKey = 'overview' | 'communications' | 'notes' | 'documents' | 'billing' | 'tasks' | 'playbook' | 'timeline' | 'calendar';

// Define tab configuration interface
interface TabConfig {
  key: TabKey;
  label: string;
  component: React.ComponentType<{ matter: MatterDetailed; onUpdate?: (updatedMatter: MatterDetailed) => void }>;
}

interface MatterTabsProps {
  matter: MatterDetailed;
  onUpdate?: (updatedMatter: MatterDetailed) => void;
  onCalendarFullscreenChange?: (isFullscreen: boolean) => void;
}

// Configure tabs with type safety and component mapping
const TABS: TabConfig[] = [
  { key: 'overview', label: 'Overview', component: OverviewTab },
  { key: 'calendar', label: 'Calendar', component: CalendarTab },
  { key: 'tasks', label: 'Tasks', component: TasksTab },
  { key: 'playbook', label: 'Playbook', component: PlaybookTab },
  { key: 'timeline', label: 'Timeline', component: TimelineTab },
  { key: 'documents', label: 'Documents', component: DocumentsTab },
  { key: 'communications', label: 'Communications', component: CommunicationsTab },
  { key: 'notes', label: 'Notes', component: NotesTab },
  { key: 'billing', label: 'Billing', component: BillingTab },
];

const MatterTabs: React.FC<MatterTabsProps> = ({ matter, onUpdate }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
  const { activeTimer, pauseTimer, startTimer, stopTimer } = useMatterBilling(matter.id);

  const handleMatterUpdate = (updatedMatter: MatterDetailed) => {
    if (onUpdate) {
      onUpdate(updatedMatter);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Tab Navigation */}
      <div className="flex-shrink-0 flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-lg overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              // Reset calendar expansion when switching away from calendar
              if (tab.key !== 'calendar') {
                setIsCalendarExpanded(false);
              }
            }}
            className={`px-3 py-2 text-sm font-medium transition-colors focus:outline-none whitespace-nowrap ${
              activeTab === tab.key
                ? 'border-b-2 border-lex-accent text-lex-accent dark:text-white bg-white dark:bg-gray-800'
                : 'text-gray-600 dark:text-gray-300 hover:text-lex-accent dark:hover:text-white hover:bg-white dark:hover:bg-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Active Timer Banner — visible across all tabs */}
      {activeTimer && (
        <ActiveTimerBanner
          activeTimer={activeTimer}
          onPause={pauseTimer}
          onResume={startTimer}
          onStop={stopTimer}
        />
      )}

      {/* Tab Content - Conditional layout based on active tab */}
      <div
        className={`flex-1 min-h-0 ${
          activeTab === 'notes'
            ? 'overflow-hidden p-4'
            : 'overflow-y-auto'
        } ${isCalendarExpanded && activeTab === 'calendar' ? 'p-0 h-full' : activeTab === 'notes' ? '' : 'p-4'}`}
      >
        {(() => {
          const currentTab = TABS.find(tab => tab.key === activeTab);
          if (!currentTab) return <div>Tab not found</div>;

          const TabComponent = currentTab.component;

          // Pass onUpdate only to tabs that need it (Overview and Notes primarily)
          const needsOnUpdate = ['overview', 'notes'].includes(activeTab);
          const props: any = { matter };

          if (needsOnUpdate) {
            props.onUpdate = handleMatterUpdate;
          }

          return (
            <div key={activeTab} className={`lex-tab-panel ${activeTab === 'notes' ? 'h-full min-h-0' : ''}`}>
              <TabComponent {...props} />
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default MatterTabs;
